// src/components/EditableElement.tsx
import React, {
	useState,
	useEffect,
	useRef,
	MouseEvent,
	KeyboardEvent,
	ChangeEvent,
} from 'react';
import { makeStyles } from '@mui/styles';
import { Theme } from '@mui/material/styles';
import { Box, Checkbox, FormControlLabel, InputBase, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

//
// 1) Props
//
export type EditableElementType = 'string' | 'number' | 'textarea' | 'counter';
export type NamePosition = 'left' | 'inside';
export interface CounterTrigger {
	value: number;
	label: string;
}

export interface CounterValue {
	value: number;
	min: number;
	max: number;
	defaultValue: number;
	triggers?: CounterTrigger[];
}

type EditableElementValue = string | number | CounterValue;

export interface EditableElementProps {
	/** Label to display above/inside the field */
	name: string;
	/** Where to render the `name` label */
	namePosition?: NamePosition;
	/** Current value (as string) */
	value?: EditableElementValue;
	/** Field type */
	type?: EditableElementType;
	/** Can user edit it? */
	editable?: boolean;
	/**
	 * Called when the user “saves” a new value.
	 * Always passed a string, even for numbers.
	 */
	onChange: (newValue: string | CounterValue) => void;
	/**
	 * Optional “roll” callback: e.g. `roll([10,10])`
	 */
	roll?: (dice: number[]) => void;
}

//
// 2) Styles
//
const useStyles = makeStyles<Theme, { type: EditableElementType }>((theme) => ({
	root: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		borderRadius: '20px',
		padding: '8px',
		width: ({ type }) => (type === 'number' ? '100px' : '300px'),
		backgroundColor: theme.palette.grey[200],
		'&:hover': {
			backgroundColor: theme.palette.grey[300],
		},
		position: 'relative',
		cursor: 'pointer',
	},
	inputContainer: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
	},
	input: {
		flexGrow: 1,
		marginLeft: theme.spacing(1),
		borderRadius: '20px',
		backgroundColor: 'transparent',
		border: 'none',
		fontSize: '16px',
		fontWeight: 'bold',
		color: theme.palette.text.primary,
		whiteSpace: 'pre-wrap', // Preserve newlines
		'&:focus': {
			outline: 'none',
			boxShadow: `0 0 0 3px ${theme.palette.primary.light}`,
		},
		textAlign: 'center',
	},
	inputValue: {
		textAlign: 'center',
	},
	name: {
		fontSize: '12px',
		color: theme.palette.text.secondary,
		textAlign: 'center',
		margin: 0,
	},
	nameTop: {
		fontSize: '12px',
		color: theme.palette.text.secondary,
		marginBottom: '8px',
	},
	iconButton: {
		position: 'absolute',
		top: '-7px',
		right: '-2px',
	},
	icon: {
		fontSize: '16px',
	},
}));

//
// 3) Component
//
const EditableElement: React.FC<EditableElementProps> = ({
	name,
	namePosition = 'left',
	value,
	type = 'string',
	editable = true,
	onChange,
	roll,
}) => {
	const classes = useStyles({ type });
	const [editing, setEditing] = useState(false);
	const hasRollAction = typeof roll === 'function';
	const [inputValue, setInputValue] = useState<string>(
		typeof value === 'string' || typeof value === 'number'
			? String(value)
			: type === 'number'
				? '0'
				: `Add your ${name} here`
	);

	const [counter, setCounter] = useState<CounterValue>({
		value: 0,
		min: 0,
		max: 10,
		defaultValue: 10,
		triggers: [],
	});

	// two refs: one for <InputBase>, one for <textarea>
	const inputRef = useRef<HTMLInputElement>(null);
	const textareaRef = useRef<HTMLTextAreaElement>(null);

	// keep in sync if `value` changes externally
	useEffect(() => {
		if (type === 'counter' && typeof value === 'object') {
			setCounter(value as CounterValue);
		} else if (typeof value === 'string' || typeof value === 'number') {
			setInputValue(String(value));
		}
	}, [value, type]);


	// when we enter edit mode, focus & (if not textarea) select
	useEffect(() => {
		if (!editing) return;
		if (type === 'textarea') {
			textareaRef.current?.focus();
		} else {
			inputRef.current?.focus();
			inputRef.current?.select();
		}
	}, [editing, type]);

	// press Enter in single-line → save
	const handleKeyDown = (e: KeyboardEvent<HTMLElement>) => {
		if (e.key === 'Enter' && type !== 'textarea') {
			save();
		}
	};

	// block non-digits if number
	const handleKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
		if (type === 'number' && !/[0-9\-]/.test(e.key)) {
			e.preventDefault();
		}
		// Prevent multiple dashes or dashes in invalid positions
		if (type === 'number' && e.key === '-' && (inputRef.current?.selectionStart !== 0 || inputValue.includes('-'))) {
			e.preventDefault();
		}
	};

	// commit changes
	const save = () => {
		setEditing(false);
		if (type === 'counter') {
			onChange(counter);
		} else {
			if (inputValue !== value) {
				if (type === 'number' && isNaN(parseFloat(inputValue))) return;
				onChange(inputValue);
			}
		}

	};

	const updateCounterField = (field: keyof CounterValue, val: string) => {
		const num = parseFloat(val);
		if (!isNaN(num)) {
			setCounter((prev) => ({ ...prev, [field]: num }));
		}
	};


	// clicking root toggles edit vs. roll
	const elementClick = (e: MouseEvent<HTMLDivElement>) => {
		e.stopPropagation();
		if (editing) return;
		if (hasRollAction && !editable) {
			roll([10, 10]);
			return;
		}
		if (editable) {
			setEditing(true);
			return;
		}
		if (hasRollAction) {
			roll([10, 10]);
		}
	};

	return (
		<div className={classes.root} onClick={elementClick}>
			{namePosition === 'left' && <div className={classes.name}>{name}</div>}

			{type === 'counter' ? (
				!editing ? (
					<>
						<Box>
							{/* Compact checkbox row */}
							<Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', flexWrap: 'wrap' }}>
								{Array.from({ length: counter.max }, (_, i) => {
									const point = i + 1; // starts at 1, not 0
									const checked = point <= counter.value;

									return (
										<Checkbox
											key={point}
											size="small"
											checked={checked}
											onChange={() => {
												let newValue = counter.value;
											  
												if (point === counter.value) {
												  // Clicking the currently selected highest point → uncheck it
												  newValue = point - 1;
												} else {
												  // Otherwise, set to the new point
												  newValue = point;
												}
											  
												const updatedCounter = { ...counter, value: newValue };
												setCounter(updatedCounter);
												onChange(updatedCounter);
											  }}
											inputProps={{ 'aria-label': `point-${point}` }}
										/>
									);
								})}
							</Box>

							{/* Trigger messages */}
							<Box mt={1}>
								{(counter.triggers ?? [])
									.filter((trigger) => counter.value >= trigger.value)
									.map((trigger, idx) => (
										<Typography key={idx} variant="caption" sx={{ textAlign: 'center', display: 'block' }}>
											{trigger.label}
										</Typography>
									))}
							</Box>
						</Box>
						{editable && (
							<IconButton
								className={classes.iconButton}
								size="small"
								type="button"
								onClick={(event) => {
									event.stopPropagation();
									setEditing(true);
								}}>
								<EditIcon className={classes.icon} />
							</IconButton>
						)}
					</>
				) : (
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
						<Box sx={{ display: 'flex', gap: 2 }}>
							{(['value', 'min', 'max', 'defaultValue'] as const).map((key) => (
								<TextField
									key={key}
									label={key}
									type="number"
									value={counter[key]}
									onChange={(e) => updateCounterField(key, e.target.value)}
									size="small"
									variant="outlined"
									fullWidth
								/>
							))}
						</Box>

						{/* Triggers list */}
						<Box>
							<strong>Triggers</strong>
							{(counter.triggers ?? []).map((trigger, index) => (
								<Box
									key={index}
									sx={{
										display: 'flex',
										gap: 1,
										alignItems: 'center',
										mt: 1,
									}}
								>
									<TextField
										label="Value"
										type="number"
										size="small"
										value={trigger.value}
										onChange={(e) => {
											const updated = [...(counter.triggers ?? [])];
											updated[index] = {
												...updated[index],
												value: parseFloat(e.target.value),
											};
											setCounter((prev) => ({ ...prev, triggers: updated }));
										}}
										sx={{ width: 100 }}
									/>
									<TextField
										label="Label"
										size="small"
										value={trigger.label}
										onChange={(e) => {
											const updated = [...(counter.triggers ?? [])];
											updated[index] = {
												...updated[index],
												label: e.target.value,
											};
											setCounter((prev) => ({ ...prev, triggers: updated }));
										}}
										fullWidth
									/>
									<Tooltip title="Remove trigger">
										<IconButton
											size="small"
											type="button"
											onClick={() => {
												const updated = [...(counter.triggers ?? [])];
												updated.splice(index, 1);
												setCounter((prev) => ({ ...prev, triggers: updated }));
											}}
										>
											<DeleteIcon fontSize="small" />
										</IconButton>
									</Tooltip>
								</Box>
							))}
							<Box mt={1}>
								<IconButton
									size="small"
									type="button"
									onClick={() => {
										const updated = [...(counter.triggers ?? []), { value: 0, label: '' }];
										setCounter((prev) => ({ ...prev, triggers: updated }));
									}}
								>
									<AddIcon fontSize="small" />
								</IconButton>
								<span>Add Trigger</span>
							</Box>
						</Box>

						{/* Save button */}
						<Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
							<IconButton
								className={classes.iconButton}
								size="small"
								type="button"
								onClick={(event) => {
									event.stopPropagation();
									save();
								}}>
								<CheckIcon className={classes.icon} />
							</IconButton>
						</Box>
					</Box>
				)
			) : (!editing ? (
				<>
					<div className={classes.input}>{inputValue}</div>
					{editable && (
						<IconButton
							className={classes.iconButton}
							size="small"
							type="button"
							onClick={(event) => {
								event.stopPropagation();
								setEditing(true);
							}}
						>
							<EditIcon className={classes.icon} />
						</IconButton>
					)}
				</>
			) : (
				<>
					{type === 'textarea' ? (
						<textarea
							className={classes.input}
							ref={textareaRef}
							value={inputValue}
							onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
								setInputValue(e.target.value)
							}
							onKeyDown={handleKeyDown}
							onBlur={save}
							rows={4}
							style={{ resize: 'none', width: '100%' }}
						/>
					) : (
						<InputBase
							className={classes.input}
							inputRef={inputRef}
							value={inputValue}
							onChange={(e: ChangeEvent<HTMLInputElement>) =>
								setInputValue(e.target.value)
							}
							onKeyDown={handleKeyDown}
							onKeyPress={handleKeyPress}
							onBlur={save}
							inputMode={type === 'number' ? 'numeric' : undefined}
							inputProps={{
								'aria-label': name,
								'data-testid': `${name}-input`,
								className: classes.inputValue,
							}}
						/>
					)}
					<IconButton
						className={classes.iconButton}
						size="small"
						type="button"
						onClick={(event) => {
							event.stopPropagation();
							save();
						}}
					>
						<CheckIcon className={classes.icon} />
					</IconButton>
				</>
			))}

			{namePosition === 'inside' && <div className={classes.name}>{name}</div>}
		</div>
	);
};

export default EditableElement;
